/**
 * ChatSystemPrompt — the single Phase 0 generation affordance.
 * (2026-10-03 P0 fix-batch design, Item 2: §3.2, §3.2.1, §3.2.2, §3.3.1, §3.6.)
 *
 * ── WHY THIS EXISTS AT ALL ─────────────────────────────────────────────────────────────────────
 * Generation used to be driven by a `Generate` button in `Phase0Page`'s header. That button was
 * enabled the moment any file existed, gave no indication it would run for ~10 minutes, and — before
 * Item 1 — could fail opaquely. Meanwhile `SourcesSheet`'s empty copy pointed the user at a "run
 * Generate" control living on a different surface. The instruction and the affordance were in two
 * places and neither explained the cost.
 *
 * This component is the one place both live. It renders inside the chat transcript, which is the
 * default view when no file tab is open, so it is the first thing on screen.
 *
 * ── WHY IT IS CLIENT-RENDERED STATE AND NOT A PERSISTED `system` MESSAGE (AD-5) ────────────────
 * `role = 'system'` IS permitted by V040's CHECK constraint, so persisting it is possible. It is
 * still wrong here:
 *   • `project_chat_messages` has NO DELETE grant (the V018/V020 audit-retention invariant). A
 *     persisted prompt could never be removed once generation completed, leaving a permanent
 *     "you haven't generated yet" row at the top of an audit-grade transcript that is ALSO the
 *     agent's conversation-history input.
 *   • It would need its own idempotency key or it would be written on every page load.
 *   • It is derived state (`sources > 0 && artifacts == 0`), and derived state stored as a fact is
 *     exactly the class of bug Item 3 is about.
 *
 * ── THE SAFETY PROPERTY (§3.3.1, H-2 / H-3) ────────────────────────────────────────────────────
 * Read `ChatSystemPromptProps.artifacts`'s doc before changing this file. `onGenerate` is the BARE
 * generate mutation with no ConflictDialog, and the only thing making that safe is that state B is
 * unreachable while the artifact queries are unresolved — which is enforced by the TYPE, not by a
 * convention.
 */

import { AlertCircle, FolderOpen, RefreshCw, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { ArtifactsState } from "@/hooks/useArtifacts";

/**
 * The `error_message` a DELIBERATE stop writes.
 *
 * Mirrors `PHASE0_STOP_SENTINEL` in `packages/projects/services/phase0-cancel.service.ts`
 * (re-exported from `packages/projects/handlers/phase0-stop.ts`). The frontend is a
 * standalone vite workspace with no dependency on `packages/*`, so this is a mirror rather than an
 * import — the same convention `ARTIFACT_LABELS` and `ACCEPTED_EXTENSIONS` already follow. A
 * drift test asserts the two stay equal, so a re-wording cannot silently turn branch D1 into dead
 * code.
 *
 * `phase0_generations.status` has no `'stopped'` value, so a user-initiated stop is recorded as
 * `'failed'`. Rendering that as "the last generation attempt failed" would tell the user their own
 * action was a system fault. See AD-5c and OQ-J.
 */
export const PHASE0_STOP_SENTINEL = "Manually stopped by user";

/**
 * The `error_message` written when a run is stopped because one of its source files was deleted
 * mid-run. Mirrors `PHASE0_SOURCE_DELETED_SENTINEL` in
 * `packages/projects/services/phase0-cancel.service.ts` (same mirror convention as above). Renders
 * as a stop, not a failure — the user deleted the file — with the next step spelled out.
 */
export const PHASE0_SOURCE_DELETED_SENTINEL =
  "Stopped: a source file was deleted";

/** Minimal shape this component needs from the `phase0_generations` row. */
export interface ChatSystemPromptGeneration {
  status: string;
  completed_at: string | null;
  error_message: string | null;
}

/**
 * ── SAFETY CONTRACT — the reason this component takes `ArtifactsState` and not a boolean ───────
 *
 * `onGenerate` is the BARE generate mutation with no ConflictDialog. It is reachable from state B
 * ONLY, and state B's precondition is
 * `artifacts.phase === 'ready' && artifacts.existing.length === 0`. Because `existing` does not
 * exist outside the `'ready'` branch, state B cannot be EVALUATED — and `onGenerate` cannot be
 * rendered — while the artifact queries are unresolved or errored. That is a type-level guarantee,
 * not a convention.
 *
 * ⚠️ Do NOT flatten this prop to `existingArtifacts: ArtifactType[]` + `artifactsLoading: boolean`.
 * It reintroduces H-2/H-3 as a two-line change: both unresolved phases collapse into "no artifacts
 * exist", and a project whose five committed artifacts failed to load renders a primary Generate
 * button that silently overwrites them. See §3.3.1 and AD-8a.
 */
export interface ChatSystemPromptProps {
  /** Three-phase artifact existence. The ONLY source of "do artifacts exist" in this component. */
  readonly artifacts: ArtifactsState;
  /** `uploaded_files.length` from the phase0 status response — drives state A and B's count copy. */
  readonly sourceCount: number;
  /** Latest `phase0_generations` row, or null when the project has never generated. */
  readonly generation: ChatSystemPromptGeneration | null;
  /** `latest_input_uploaded_at` (§3.4). Null when there are no inputs. Drives state C. */
  readonly latestInputUploadedAt: string | null;
  /** False → read-only copy, no action buttons anywhere, no regenerate line. */
  readonly canManage: boolean;
  /** True while a job is `pending` or `generating`. Suppresses all states (the banner owns it). */
  readonly isGenerating: boolean;
  /** `'docked'` renders null — a docked chat lives inside a file that already exists. */
  readonly variant: "full" | "docked";

  /** State B ONLY. The bare `generate` mutation — see the safety contract above. */
  readonly onGenerate: () => void;
  /** States C and D and the §3.2.2 line. `handleRegenerateClick` — keeps the ConflictDialog path. */
  readonly onRegenerate: () => void;
  /** State A. Opens `SourcesSheet`. */
  readonly onOpenSources: () => void;
}

/**
 * Shared container.
 *
 * `role="note"` — NOT `role="alert"` or `role="status"`. The transcript container is already the
 * subtree's single live region (`role="log" aria-live="polite"` in `ChatTranscript`), and nested
 * live regions double-announce in JAWS and NVDA. A `note` inside the log is announced once, by the
 * log, on insertion. This applies to ALL renders including the skeleton and state E — see §3.6.
 */
function PromptShell({
  children,
  busy,
  label,
  tone = "neutral",
}: {
  children: React.ReactNode;
  busy?: boolean;
  label?: string;
  tone?: "neutral" | "error";
}): JSX.Element {
  return (
    <div
      role="note"
      {...(busy ? { "aria-busy": "true" } : {})}
      {...(label ? { "aria-label": label } : {})}
      data-testid="chat-system-prompt"
      className={[
        "mb-4 rounded-lg border border-outlines p-4",
        tone === "error"
          ? "border-error bg-badge-red-bg"
          : "border-outlines bg-subtle-bg",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm font-semibold text-text-primary">{children}</p>;
}

function Body({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="mt-1 text-sm text-text-secondary">{children}</p>;
}

/** `{n} source document{s}` with correct agreement — used by states A, B and C. */
function plural(n: number): string {
  return n === 1 ? "document" : "documents";
}

export function ChatSystemPrompt({
  artifacts,
  sourceCount,
  generation,
  latestInputUploadedAt,
  canManage,
  isGenerating,
  variant,
  onGenerate,
  onRegenerate,
  onOpenSources,
}: ChatSystemPromptProps): JSX.Element | null {
  // ── Precedence, in source order (§3.3.1) ──────────────────────────────────────────────────────
  //
  //   docked            → null
  //   'loading'         → skeleton                    ← no action reachable
  //   'error'           → state E (Retry only)        ← no Generate reachable
  //   isGenerating      → null (the banner owns this period)
  //   then D → A → B → C, first match wins
  //   plus the non-exclusive §3.2.2 regenerate line
  //
  // The resolution gate is checked BEFORE `isGenerating` rather than after. Either order is safe —
  // neither exposes a Generate control — but gate-first gives one statable invariant: OUTSIDE the
  // 'ready' PHASE, THE ONLY REACHABLE ACTION IN THIS COMPONENT IS `Retry`. That is a property a
  // reviewer can check by reading the first three branches. A per-branch exemption list is not.

  // A docked chat lives inside a file, which by definition already exists.
  if (variant === "docked") return null;

  if (artifacts.phase === "loading") {
    // A NEUTRAL skeleton — no state card and no action button of any kind, including §3.2.2's line.
    // State A is suppressed here too, even though it is harmless: a per-state exemption list is not
    // verifiable, whereas "nothing is reachable outside 'ready'" is.
    return (
      <PromptShell busy label="Loading project state">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="mt-2 h-4 w-1/2" />
      </PromptShell>
    );
  }

  if (artifacts.phase === "error") {
    // ── State E ──────────────────────────────────────────────────────────────────────────────────
    // Distinct from the skeleton deliberately: a skeleton that never resolves is a hung UI with no
    // stated cause and no way out, and `useArtifact` sets `retry: false` so this phase does not
    // self-heal. The user needs to be told it is a LOAD failure — not a project problem — and given
    // the one action that can fix it.
    return (
      <PromptShell tone="error">
        <div className="flex items-start gap-2">
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-error"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <Heading>
              Couldn&apos;t check which documents have been generated.
            </Heading>
            <Body>
              This is a loading problem, not a problem with your project —
              nothing has changed. Retry, or reload the page.
            </Body>
            <div className="mt-3">
              {/* `artifacts.retry` rather than an `onRetry` prop: the action only exists in the
                  error phase, so it is impossible to call in any other. Same discipline as
                  co-locating `existing` with 'ready'. */}
              <Button variant="outline" size="sm" onClick={artifacts.retry}>
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </PromptShell>
    );
  }

  // From here on `artifacts.phase === 'ready'`, so `artifacts.existing` is reachable and COMPLETE.
  const existing = artifacts.existing;

  // The generating banner in `Phase0Page` owns this period — a second surface describing the same
  // in-flight job would just be noise next to it.
  if (isGenerating) return null;

  const failed = generation?.status === "failed";
  const hasArtifacts = existing.length > 0;

  // §3.2.2 — the non-exclusive low-emphasis line. NOT a sixth state: it renders beneath whichever
  // of C/D matched AND in the steady state where none of A-D matched.
  //
  // Retained rather than removed (AD-5a): today's header `Regenerate` is unconditional, so a user
  // who simply dislikes the output can re-roll it at any time. None of states A-D fires for
  // "artifacts exist, sources unchanged, I just want another attempt" — C needs newer inputs, D
  // needs a failure. Removing it would leave the user's only recourse as TOUCHING A SOURCE FILE to
  // make state C fire, which teaches them to game the state machine.
  //
  // Low-emphasis because it is a ~10-minute, five-artifact Bedrock run that overwrites drafts, and
  // it must not sit at the same visual weight as the explained primary actions.
  const regenerateLine =
    hasArtifacts && canManage ? (
      <p className="mt-3">
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-text-secondary"
          onClick={onRegenerate}
        >
          Regenerate all five artifacts from the current sources
        </Button>
      </p>
    ) : null;

  // ── State D — last run failed or was stopped (§3.2.1, H-1) ───────────────────────────────────
  //
  // Evaluated BEFORE A/B/C. Without it the three-state machine rendered NOTHING here, because
  // `phase0-generate` stamps `completed_at` on FAILURE as well as success:
  //   A false (files exist) · B false (the previous run's artifacts survive) · C false
  //   (`completed_at` was stamped by the failure, so it is newer than every input)
  // — and this item deletes the header Generate/Regenerate buttons, so the only generation entry
  // point in the product would be gone. For the project that motivated this document, whose latest
  // run failed with the Item 1 500, Item 2 without state D would have removed its only way to retry.
  if (failed) {
    const raw = generation?.error_message;
    const sourceDeleted = raw === PHASE0_SOURCE_DELETED_SENTINEL;
    const stopped = raw === PHASE0_STOP_SENTINEL || sourceDeleted;
    const hasDetail = typeof raw === "string" && raw.trim().length > 0;

    return (
      <PromptShell tone="error">
        <div className="flex items-start gap-2">
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-error"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            {/* D1 — a deliberate stop is not a fault. */}
            <Heading>
              {stopped
                ? "Generation was stopped."
                : "The last generation attempt failed."}
            </Heading>
            <Body>
              {stopped
                ? sourceDeleted
                  ? "A file used by this run was deleted. Upload the replacement and generate again."
                  : "You stopped the last run before it finished."
                : hasDetail
                  ? raw
                  : // D3 — `error_message` is nullable and nothing in the schema requires it.
                    "No error detail was recorded. Try again — if it fails a second time, the message will usually say why."}
              {hasArtifacts &&
                " Your existing artifacts are unchanged and still open as tabs."}
            </Body>
            {canManage && (
              <div className="mt-3">
                {/* `onRegenerate` (= handleRegenerateClick), NOT the bare mutation: a failed run
                    does not unlock committed artifacts, so a retry can still collide with them and
                    must still raise the ConflictDialog. Routing D to the bare mutation would
                    silently skip that dialog on exactly the path a user is most likely to click
                    repeatedly. */}
                <Button size="sm" onClick={onRegenerate}>
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Try again
                </Button>
              </div>
            )}
            {/* §3.2.2's line is non-exclusive and renders beneath whichever of C/D matched. In D it
                is the same handler as `Try again`, and that redundancy is deliberate: the wording
                states explicitly that a retry redrafts ALL FIVE artifacts from the current sources,
                which `Try again` alone does not say. */}
            {regenerateLine}
          </div>
        </div>
      </PromptShell>
    );
  }

  // ── State A — no sources ─────────────────────────────────────────────────────────────────────
  //
  // D wins over A when a failed run is followed by deleting every source (L-8). Accepted: clicking
  // `Try again` with zero files hits the §2.5 guard at hop 1 and returns 422 within seconds (no
  // Bedrock spend), and that error code auto-opens the Sources panel — exactly where A would have
  // sent them. Reordering was rejected because it would hide a real failure message behind an upload
  // prompt on every project whose run failed and whose sources were since cleaned up.
  if (sourceCount === 0) {
    return (
      <PromptShell>
        <Heading>Upload your source documents to begin.</Heading>
        <Body>
          Add the SOW and any presales transcripts. Supported formats: DOCX,
          PDF, TXT, MD.
        </Body>
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={onOpenSources}>
            <FolderOpen className="h-4 w-4" aria-hidden="true" />
            Open Sources
          </Button>
        </div>
      </PromptShell>
    );
  }

  // ── State B — sources, no artifacts ──────────────────────────────────────────────────────────
  //
  // The ONLY state that may use the bare `onGenerate` mutation, and only because its precondition
  // is `existing.length === 0` — there is provably nothing to conflict with. That precondition is
  // only trustworthy because of the resolution gate above.
  if (!hasArtifacts) {
    return (
      <PromptShell>
        <Heading>
          I can see {sourceCount} source {plural(sourceCount)} but the Phase 0
          artifacts haven&apos;t been generated yet.
        </Heading>
        <Body>
          Generating produces all five artifacts — Preliminary SRS, Discovery
          Agenda, Project Plan, Jira Backlog, Kickoff Deck. It takes up to 10
          minutes. After that you can refine any of them by tagging it here and
          describing the change.
        </Body>
        {canManage && (
          <div className="mt-3">
            <Button size="sm" onClick={onGenerate}>
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Generate artifacts
            </Button>
          </div>
        )}
      </PromptShell>
    );
  }

  // ── State C — sources newer than artifacts ───────────────────────────────────────────────────
  //
  // `completed_at` null → C does not fire: a failed or in-flight job is not "artifacts older than
  // sources", and state D / the generating banner already cover those.
  const inputsAreNewer =
    latestInputUploadedAt !== null &&
    generation?.completed_at != null &&
    new Date(latestInputUploadedAt) > new Date(generation.completed_at);

  if (inputsAreNewer) {
    return (
      <PromptShell>
        <Heading>
          {sourceCount} source {plural(sourceCount)} changed since the last
          generation.
        </Heading>
        <Body>
          Regenerating redrafts all five artifacts from the current sources.
          Files you have committed are preserved — they are skipped and
          reported.
        </Body>
        {canManage && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={onRegenerate}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Regenerate from sources
            </Button>
          </div>
        )}
        {regenerateLine}
      </PromptShell>
    );
  }

  // ── Steady state — none of A-D matched. Only the low-emphasis line, with no card. ────────────
  if (!regenerateLine) return null;

  return (
    <div role="note" data-testid="chat-system-prompt" className="mb-2">
      {regenerateLine}
    </div>
  );
}

export default ChatSystemPrompt;
